// RoastLog
import { getRoasters, getRoastingEvents, getStarredRoast, getUserProfile, sync } from '$lib/api.js';
import { DEFAULT_ROASTER_SETTINGS } from '$lib/constants';
import logger from '$lib/logger';
import {
  beans,
  locations,
  resourceVersions,
  roastables,
  roasters,
  roasts,
  selectedLocation,
  selectedRoaster,
  starredRoasts,
  userProfile
} from '$lib/PersistedState.svelte';
import { sortByName } from '$lib/RLogUtils.js';
import { roastingEvents } from '$lib/RoastingEvents.svelte';
import { initialDataLoad } from '$lib/State.svelte';
import type {
  Beans,
  Nullable,
  Roastables,
  Roaster,
  RoasterMapping,
  Roasters,
  Roasts,
  SyncApiResonse,
  UserProfile
} from '$lib/types/RoastLog';

// Others
import { datadogRum } from '@datadog/browser-rum';

export const fetchAndStoreInitialDataByLocation = async (
  locationId: number
): Promise<SyncApiResonse> => {
  logger.info(`Running initial data fetch and load for ${locationId}`);
  // Make the call to sync the data
  const response: SyncApiResonse = (await sync(locationId)).data;

  // Save all of the data to the stores
  beans.current = sortByName([...beans.current, ...response.beans]);

  roasts.current = [...roasts.current, ...response.roasts];

  roastables.current = sortByName([...roastables.current, ...response.roastables]);

  logger.info('Initial data load complete', {
    numBeans: response.beans.length,
    numRoasts: response.roasts.length,
    locationId,
    numRoastables: response.roastables.length
  });

  // Save the roasters by id so that it's easier to get them using the selected roaster Id.
  storeRoasters(response.roasters, locationId);
  // Get the roasting events
  fetchAndStoreRoastingEvents();

  // Let's set an initial roaster too. To make it easy, simply pick the first one since we have no
  // way to setting a "preferred" roaster right now.
  if (response.roasters.length > 0) {
    const r = response.roasters[0];
    selectedRoaster.roasterId = r.id.toString();
  }

  // These are simply overwritten since they are not versioned through the API.
  locations.current = response.locations;
  resourceVersions.current = response.versions;

  initialDataLoad.current = false;

  return response;
};

export const fetchAndStoreDataByLocation = async (locationId: number): Promise<SyncApiResonse> => {
  // Sync the data, but also request the outdated data by passing true as the second argument. This
  // API takes care of fetching the latest version from the version store.
  const response: SyncApiResonse = (await sync(locationId, true)).data;

  beans.current = reconcileActiveItems(beans.current, response.beans) as Beans;
  roastables.current = reconcileActiveItems(roastables.current, response.roastables);
  roasts.current = reconcileActiveRoasts(roasts.current, response.roasts);

  const currentRoasters = Object.values(roasters.current);
  const activeRoasters = reconcileActiveItems(currentRoasters, response.roasters) as Roasters;
  storeRoasters(activeRoasters, locationId);

  locations.current = response.locations;
  resourceVersions.current = response.versions;

  return response;
};

export const fetchStarredRoast = (roastId: number) => {
  return getStarredRoast(roastId).then((roast) => {
    starredRoasts.value[roastId] = roast;
  });
};

/**
 * Helper to remove old roasts from the store, and add new ones to the store.
 * @param roasts List of roasts
 */
const reconcileActiveRoasts = (existingRoasts: Roasts, roasts: Roasts) => {
  // First, handle active/inactive roasts
  const roastsToAdd = [];
  const roastIdsToRemove: (number | string)[] = [];

  for (const r of roasts) {
    if (r.is_active && r.starred) {
      roastsToAdd.push(r);
    } else {
      roastIdsToRemove.push(r.id);
    }
  }

  // Remove inactive roasts from existing roasts
  const activeRoasts = existingRoasts.filter((r) => !roastIdsToRemove.includes(r.id));

  // Create a map of existing roasts for easier lookup
  const roastMap = new Map(activeRoasts.map((roast) => [roast.id, roast]));

  // Update or add new roasts. This will overwrite if id exists, or add if it doesn't
  for (const newRoast of roastsToAdd) {
    roastMap.set(newRoast.id, newRoast);
  }

  // Convert map back to array
  const updatedRoasts = Array.from(roastMap.values());

  return sortByName(updatedRoasts);
};

const reconcileActiveItems = (
  existingItems: Beans | Roastables | Roasters,
  items: Beans | Roastables | Roasters
) => {
  const itemsToAdd = [];
  const itemIdsToRemove: (number | string)[] = [];

  for (const r of items) {
    if (r.is_active) {
      itemsToAdd.push(r);
    } else {
      itemIdsToRemove.push(r.id);
    }
  }

  const activeItems = existingItems.filter((b) => !itemIdsToRemove.includes(b.id));
  const combinedItems = [...activeItems, ...itemsToAdd];

  // Use reduce to keep items with highest version for each id
  const deduplicatedItems = Object.values(
    combinedItems.reduce(
      (acc, item) => {
        if (!acc[item.id] || acc[item.id].version < item.version) {
          acc[item.id] = item;
        }
        return acc;
      },
      {} as { [key: string | number]: (typeof combinedItems)[0] }
    )
  );

  return sortByName(deduplicatedItems);
};

export const fetchAndStoreRoasters = async (locationId: number): Promise<void> => {
  const roasters = await getRoasters();

  const patchedRoasters = roasters.map((r) => {
    if (!r.settings.thermocouples) {
      r.settings.thermocouples = DEFAULT_ROASTER_SETTINGS;
    }
    return r;
  });

  storeRoasters(patchedRoasters, locationId);
};

// Save the roasters by id so that it's easier to get them using the selected roaster Id.
const storeRoasters = (roasterList: Roasters, locationId: Nullable<number> = null) => {
  if (locationId) {
    // Filter out roasters that don't match the locationId.
    // However, include the roasters with a null location, which is most of them. We don't know
    // where they are, so present them to the user and let them choose. Our newer customers may have
    // multiple locations, but most of our existing customers only have a single location, so this
    // won't have any effect.
    roasterList = roasterList.filter(
      (r: Roaster) => r.location_id === null || r.location_id === locationId
    );
  }

  const roastersById = roasterList.reduce((map: RoasterMapping, r: Roaster) => {
    map[r.id] = r;
    return map;
  }, {});

  roasters.current = roastersById;
};

export const fetchAndStoreRoastingEvents = async () => {
  const events = await getRoastingEvents();
  roastingEvents.items = events;
  logger.info('Roasting events load complete', {
    numEvents: events.length
  });
};

export const fetchAndStoreUserProfile = async (): Promise<UserProfile> => {
  const profile = await getUserProfile();
  userProfile.current = profile;

  // Also set the users preferred location
  selectedLocation.current = profile.location.id.toString();

  datadogRum.setUser({
    id: profile.id.toString(),
    name: `${profile.first_name} ${profile.last_name}`,
    email: profile.email,
    username: profile.username,
    account: profile.account.name
  });

  return profile;
};
